/**
 * The coach on screen: one cue at a time, and the ONE place a cue is pressed.
 *
 * `core/cues.ts` emits data — a cue's action is a descriptor, never a closure —
 * so nothing spends until somebody presses something here. `takeCueAction` is
 * that press, and it is deliberately the only dispatcher in the app: the card,
 * the keyboard and the pins all route through it, so a cue cannot come to mean
 * one thing under the mouse and another under `Enter`.
 *
 * The other half of that rule is `visibleCues`. Two surfaces each computing
 * their own order is how `Enter` comes to act a cue the card is not showing, so
 * the card, the bar, the director and the keyboard all read that one helper —
 * which lives in `coachView.ts`, along with the gate and the price extras,
 * because the canvas reads them too and this file renders.
 */

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react'
import { onAfterDraw } from '../canvas/frameBus'
import { screen } from '../canvas/screen'
import { useGraphFrame } from '../canvas/useGraphFrame'
import { track } from '../core/analytics'
import { useStore } from '../core/store'
import { undoOffer } from '../core/undoOffer'
import { CUE_LABEL, CUE_PRICE_VERB, type Cue } from '../core/cues'
import type { ModelEntry } from '../core/models'
import { joinCandidate } from '../core/rung'
import { asUpNextVerb } from '../core/upNext'
import { runAudit, runLinkScan } from '../engine/reason'
import { scrollToOutcomeForm } from './Grounding'
import { groupReports } from './groupPress'
import { afterSelection } from './promotePress'
import { pressRefusal, pressVerb } from './upNextPress'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import {
  restingEstimate,
  restingLabel,
  restingPlan,
  restingTitle,
  takeRestingPress,
} from './restingPress'
import { Icon } from './components/Icon'
import { VerbPrice } from './VerbPrice'
import {
  CARD_EDGE,
  CARD_MIN_H,
  DOCK_ROOM,
  cardPlace,
  clampCardX,
  type Band,
  type CardSpot,
  type Point,
} from './coachPlace'
import {
  cueAuditor,
  cuePressName,
  cuePressTail,
  cueRefusal,
  liveCoach,
  useCardShows,
  useCoachHeading,
  useLiveCoach,
  useVisibleCues,
  visibleCues,
} from './coachView'

/**
 * Remove a branch, and say so with the way back.
 *
 * Exported because the coach's `prune` cue must press EXACTLY this — the mark,
 * the count, and the toast whose undo is checked against the history entry it
 * belongs to. Two copies of that is how one of them would come to pop the wrong
 * entry; the inspector's footer calls it too, so there is one writer.
 */
export function prunePress(nodeId: string): void {
  const s = useStore.getState()
  const node = s.session.nodes[nodeId]
  if (!node || node.parentId === null) return
  s.mark('Prune')
  const n = s.dropSubtree(nodeId)
  // The recovery is a tappable action, not a named keystroke — "⌘Z to put it
  // back" was a route that does not exist on a phone, where Prune is fully
  // reachable in the sheet's footer. ⌘Z still works. The offer restores THIS
  // step and no other (`core/undoOffer.ts`), which the rewrite's toast shares.
  const entry = useStore.getState().past[useStore.getState().past.length - 1]
  if (!n) return
  useStore
    .getState()
    .toast(`Pruned ${n} node${n === 1 ? '' : 's'}.`, 'info', undoOffer(entry, 'Put it back'))
}

/**
 * The one consequential press.
 *
 * Retirement comes first for everything that can actually happen: the press is
 * the answer, and a failure downstream — a model that errors, a bridge end
 * pruned between the recompute and the click — must not leave the same cue
 * sitting at the top of the list to be pressed again. Selection lands before
 * anything runs, so the ordinary Node panel has the branch before it changes
 * under the reader.
 *
 * What comes BEFORE retirement is the one class of press that cannot happen at
 * all: a refusal. `cueRefusal` asks the engine's own questions — a routed role
 * with no key, a model that cannot search, the other house a cross-examination
 * needs — and where the answer is a sentence, this returns having said it and
 * having changed nothing. Retiring on a refusal would spend the instance on a
 * pass that never ran, and the way back (`unmuteCues`) is itself disabled on the
 * bar while nothing has been put away. The card disables its button on the same
 * reading, so the mouse and `Enter` refuse together; the digest's row keeps its
 * button and gets the sentence out loud, because a row has no space for the
 * paragraph the card prints.
 *
 * A check, a Deepen, a Challenge, a conjecture pass or a prior-art search is the
 * key's own press, and goes through the key's path (`pressVerb`): run now, or —
 * met by a pass in flight — queued on the Up next line behind it, which is a
 * press taken, so the cue retires as acted (D162, D286). The line's own refusal
 * (full, already up next, a live run) is asked BEFORE retirement with the
 * refusals above, for their reason. The coach's
 * surfaces render behind `coachGateOpen`, which a pass in flight shuts, so today
 * this arm is met only by a click already on its way when the lock takes hold
 * and before the card comes down — which used to retire the cue and then have
 * the verb refuse, spending the instance on a pass that never ran.
 */
export function takeCueAction(cue: Cue): void {
  const s = useStore.getState()

  const verb = cue.action ? asUpNextVerb(cue.action.kind) : null
  const refusal = cueRefusal(s, cue) ?? (verb ? pressRefusal(s, verb, cue.anchorId) : null)
  if (refusal) {
    // Said out loud rather than swallowed: a press that quietly does nothing is
    // the confusion this refuses. No `cue_acted` either — nothing was acted on.
    s.toast(refusal, 'warn')
    return
  }

  s.retireCue(cue.key, cue.kind, 'acted')
  // After retirement, so the event and the state agree about what happened, and
  // never on the refusal above. The KIND only — a closed union, exactly the
  // `track('exploration_completed', { stop })` shape. No title, no body, no
  // anchor id: what was explored never reaches this file.
  track('cue_acted', { kind: cue.kind })
  const action = cue.action
  if (!action) return

  // The one action about the RUN rather than about a node. Selecting a node for
  // it would move the reader's working state to make a point about a report.
  if (action.kind === 'report') {
    s.setRail(true)
    s.setRightTab('run')
    // …and the card puts itself down. This is the one action whose whole content
    // is something to READ, at length, in the pane this press just opened — so
    // leaving the card up would float the next cue over the account the press
    // just asked for. It held when the card lived inside that pane and it holds
    // now that the card floats beside its node: either way the reader asked for
    // the report and got a cue talking over it. The bar and the pins stay: the
    // reader has not finished with the coach, only with this card. `G` brings it
    // straight back.
    s.dismissCoach()
    return
  }

  // The other press about the graph rather than the anchor: a checks-only run over
  // the claims most of the board rests on, which the anchor is one of only if the
  // count says so. Selecting it would put the reader on a claim the run may not
  // touch, and the run's own pod is where its progress shows.
  if (action.kind === 'checkResting') {
    takeRestingPress()
    return
  }

  s.select(cue.anchorId)
  s.setRail(true)
  s.setRightTab('node')

  switch (action.kind) {
    case 'verify':
    case 'deepen':
    case 'challenge':
    // A conjecture pass is aimed at the record itself, so what lands hangs
    // beneath it — which is what retires the cue, by the parentage it fired on.
    // It and the prior-art search used to be dispatched straight to their
    // passes after the cue retired, so one met by the lock spent the cue on a
    // pass that then refused (D286).
    case 'conjecture':
    case 'priorArt':
      // The key's path, which runs it now or queues it behind the pass in
      // flight — asked above, synchronously, so it cannot refuse here.
      pressVerb(action.kind, cue.anchorId)
      return
    case 'audit':
      // The inspector's cross-examination path, unchanged: `runAudit` resolves
      // the other house itself and refuses when there is no key for one
      // (invariant 4). The card states that refusal rather than hiding the cue.
      void runAudit(cue.anchorId)
      return
    case 'record':
      // The form belongs to OutcomeBlock, and its one open-signal is the store's
      // `sigRecord` — the same wire the R key and the inspector's FREE row pull.
      // NOT a DOM poke: with no outcome recorded the block renders explanatory
      // text and no button, so First Read's click-the-button approach opened
      // nothing for exactly the nodes this cue is about. The scroll stays — the
      // form should arrive on screen, not merely open somewhere below the fold —
      // and it now lives beside the attribute it looks for, so this press and
      // the promote press cannot go separately blind if that is renamed.
      // A frame after the selection above, and abandoned if the reader has
      // moved on inside it — `afterSelection` in `promotePress.ts` carries both
      // halves of the reasoning and is shared rather than copied.
      afterSelection(cue.anchorId, () => {
        useStore.getState().pokeRecord()
        scrollToOutcomeForm(cue.anchorId)
      })
      return
    case 'prune':
      prunePress(cue.anchorId)
      return
    case 'group':
      // Human same-family overrides, written where the Lab's form writes them;
      // the press reads the claim's group again rather than trusting the cue's.
      groupReports(cue.anchorId)
      return
    case 'linkScan': {
      // The pair the `join` cue described, resolved from the document rather
      // than carried on the cue: a cue is JSON, and a bridge id frozen into one
      // could name a pair a prune has since made unreadable. Same predicate the
      // cue used — unsearched, both ends still readable, this anchor an end of
      // it — and the first in ladder order among those, which is the one it
      // picked. Never the score: see `core/rung.ts`.
      const best = joinCandidate(useStore.getState().session, cue.anchorId)
      // No pair left to search: the selection above is the whole press, which is
      // honest — it puts the reader on the claim the cue was about.
      if (best) void runLinkScan(best.id)
      return
    }
    case 'inspect':
      return
  }
}

/**
 * A cue's button text: its label, and the figure where the press spends.
 *
 * One component for the card and the digest row, so the two cannot print two labels
 * for one press. Every kind but one is its descriptor's label beside `VerbPrice`;
 * `checkResting` is priced as a run, and names as many claims as it will actually
 * check, both read off `restingPlan` — the same reading the press itself takes. Its
 * figure is printed in the label rather than revealed on hover, because the press
 * promises it in so many words: "· est. $X".
 */
export function CuePressText({ cue, auditor }: { cue: Cue; auditor: ModelEntry | null }) {
  const action = cue.action
  if (!action) return null
  if (action.kind === 'checkResting') return <RestingPressText />
  const verb = CUE_PRICE_VERB[action.kind]
  return (
    <>
      {action.label}
      {verb && <VerbPrice verb={verb} auditor={auditor} />}
    </>
  )
}

function RestingPressText() {
  const session = useStore((s) => s.session)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const runCeiling = useStore((s) => s.runCeiling)
  // Its figure includes the run's wrap-up while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const plan = restingPlan({ session, modelId, roleModels, runCeiling })
  const est = restingEstimate(plan)
  return (
    <span title={restingTitle(plan) || undefined}>
      {restingLabel(plan.targets.length)}
      {est ? ` · ${est}` : ''}
    </span>
  )
}

/**
 * The other two presses that answer a cue, each with exactly one writer.
 *
 * `takeCueAction` is the one dispatcher for a cue's ACTION, and these are the
 * same rule for the two ways of declining one: the card's Skip and the `S` key
 * are one function, so the skip count and the event cannot come to disagree
 * about how many times a kind was pushed away. Kinds only, as above.
 */
export function skipCue(cue: Cue): void {
  useStore.getState().retireCue(cue.key, cue.kind, 'skipped')
  track('cue_skipped', { kind: cue.kind })
}

/** Silence a kind for the rest of this exploration. The WHY fold's button is its
 *  one caller; the store action itself stays free of the event so the tests that
 *  drive it directly are not measuring anything. */
export function muteCuePress(cue: Cue): void {
  useStore.getState().muteCueKind(cue.kind, cue.key)
  track('cue_muted', { kind: cue.kind })
}

/* ── the card ───────────────────────────────────────────────────────────────
 *
 * A POPUP ON THE GRAPH, beside the node its cue is about — the design sheet's
 * own form, and the position the layer shipped without. It stood in the
 * inspector's shell for one step: mounted where First Read had stood, which made
 * the diff small and made the card a panel about a node you could not see while
 * reading about it. Everything contractual about a cue survived the move; what
 * changed is where the sentence is printed and what has to be open for it to be
 * printed at all (D25).
 *
 * Two forms, one content:
 *
 *   POPUP (>= 768px) — placed per frame from `screen.place(anchorId)` under the
 *   `CoachPins` discipline. React renders the CONTENT; one `onAfterDraw`
 *   subscription writes the seat's transform, so a camera flight or a graph
 *   growing under it never re-renders the card. `screen.place` is valid ONLY
 *   inside that callback; anywhere else it is last frame's camera.
 *
 *   COLUMN (< 768px) — a row in the run-done bar's own column, mounted by
 *   `RunDoneBar` so the two share their edges by construction rather than by a
 *   second copy of a measurement. There is no room beside a node on a phone and
 *   no pointer precision to spend on a 40px hairline.
 *
 * The card is NOT registered in `Labels.tsx`'s chrome-avoidance list, for the
 * pins' own reason: that list holds fixed chrome whose rectangle a caption must
 * never be placed under, and this is a node-anchored mover — registering it
 * would exile a band of captions that travels with the graph. It READS the
 * chrome instead, which is the other half of the same fact.
 */

/** Clearance under the run-done column, matching the gap between its own rows. */
const CARD_BAR_GAP = 8
/**
 * The field's bottom furniture, which lifts the card's floor where it meets it.
 *
 * By class, and measured, exactly as `CoachPins` measures the same corners: the
 * composer is a centred column whose glass takes every press inside it, the
 * graph's footer (D369) is the field's whole bottom edge, and the minimap stands
 * on it in the corner; all three move with the panes' insets or are absent
 * entirely at some widths. No element, no rect.
 *
 * The composer is the one that earns the rule on its own — it is 660px wide and
 * sits where a low anchor puts a card — but the argument is the corners' too, so
 * the list is the honest shape of it rather than one special case.
 */
const CARD_FLOOR = ['.slot-composer', '.graphfoot', '.minimap'] as const

/**
 * Which cue the card is showing, and the readings its content needs.
 * Exported for the screen-reader test, which renders the card from one.
 *
 * The clamp is the one the director, the pins and the canvas highlight read —
 * the visible list is derived and the state cannot know its length, so a cue
 * retired between a press and a paint lands here as "show the last one" rather
 * than as a blank card. Two readings of it is how `Enter` comes to act a cue the
 * card is not showing.
 */
export interface Shown {
  cue: Cue
  index: number
  count: number
  /** The anchor is behind a fold or a display switch (the card never shows off
   *  the replay's now): there is nothing on the graph to stand beside, so the
   *  card says so, offers "Show it", and takes the fallback position under the
   *  bar's column. */
  hidden: boolean
  /** The WHY fold, and whether this kind has been pushed away once already. Read
   *  here rather than in the content, so the content takes a live coach as a
   *  fact rather than re-asking a question its mount has already answered. */
  why: boolean
  skippedOnce: boolean
  /** The other nodes the cue describes that are drawn right now — the ones the
   *  popup must not stand on as well as its anchor (`cardPlace`). A node behind a
   *  fold has no position to avoid. */
  alsoIds: string[]
}

function useShownCue(): Shown | null {
  const coach = useLiveCoach()
  const cues = useVisibleCues()
  const shows = useCardShows()
  const { visible } = useGraphFrame()
  if (!shows || !coach) return null
  const index = Math.min(Math.max(0, coach.index), cues.length - 1)
  const cue = cues[index]!
  return {
    cue,
    index,
    count: cues.length,
    hidden: !visible.has(cue.anchorId),
    why: coach.why,
    skippedOnce: (coach.skips[cue.kind] ?? 0) === 1,
    alsoIds: cue.alsoIds.filter((id) => visible.has(id)),
  }
}

/**
 * Whether a clamped block is hiding words — read off the rendered text, never
 * guessed from its length: `scrollHeight` past `clientHeight`, the reading the
 * seed caption's measure pass takes for the same question (D98).
 *
 * Asked again whenever the words change, because a clamped box that swaps one
 * long text for another keeps its height, and `ResizeObserver` reports only a
 * box that changes. The observer covers the rest: the phone's column is as wide
 * as the screen, so turning it re-wraps the words. While a block is not clamped
 * there is nothing to measure, and the last answer stands — it is what keeps the
 * door on screen to fold the block back up.
 */
function useCut(el: RefObject<HTMLElement | null>, clamped: boolean, words: string): boolean {
  const [cut, setCut] = useState(false)
  // Layout, not effect: the door is decided before the browser paints, so a
  // card never shows for one frame with words missing and nothing saying so.
  useLayoutEffect(() => {
    const node = el.current
    if (!node || !clamped) return
    const measure = () => setCut(node.scrollHeight - node.clientHeight > 1)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    return () => ro.disconnect()
  }, [el, clamped, words])
  return cut
}

/**
 * The card's content. Byte-for-byte the shipped card: eyebrow, Skip, kind line,
 * title, body, metric, the two notes, the refusal, the WHY fold, and the row of
 * presses. Only the shell around it moved. Exported for the screen-reader test.
 *
 * ONE sentence of it is live: the kind line and the title, together
 * (`.coach__headline`). The whole card was the region once, so every change
 * inside it was read out — stepping to the next cue read the eyebrow, Skip, the
 * body, the metric, every button and the price pair on the action, about four
 * hundred characters, and opening Why read all of it again with the rule on the
 * end. What changes when the reader steps is which claim the card is about, and
 * that is what is said; the rest is there to be read at their own pace, and the
 * Why button already says it opened. The card's arrival is not announced by the
 * region — a region inserted holding its words is never heard — but by focus,
 * which lands on the card and reads its heading and, as its description, the
 * same headline.
 *
 * The card keeps one size whatever its words run to. The title is the anchor's
 * label, and the seed's label is the whole prompt (D90): a pasted brief made the
 * card a column of display type the height of the stage, with its body and its
 * presses scrolled out of sight inside it. So the title clamps at two lines and
 * the body at the longest the coach writes, and wherever a clamp is hiding words
 * a door says so, under the words it cut — D98's rule for the seed's caption,
 * at the card. The seed's door opens the prompt sheet, the reader that caption's
 * door opens, because a page of brief is read badly in a 300px card. Every other
 * door opens the card's own words where they stand. The clamps are visual only,
 * so a screen reader still reads every word.
 */
export function CoachCardBody({
  shown,
  elRef,
  form,
}: {
  shown: Shown
  elRef: RefObject<HTMLElement | null>
  form: 'popup' | 'column'
}) {
  const session = useStore((s) => s.session)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const preferCorpus = useStore((s) => s.preferCorpus)
  // The three a run-starting press is refused on — see `RefusalView`.
  const runCeiling = useStore((s) => s.runCeiling)
  const run = useStore((s) => s.run)
  const busy = useStore((s) => s.busy)
  const setCoachIndex = useStore((s) => s.setCoachIndex)
  const toggleCoachWhy = useStore((s) => s.toggleCoachWhy)
  const showNode = useStore((s) => s.showNode)
  const setPromptOpen = useStore((s) => s.setPromptOpen)
  // Read off the record, never off the door that opened the card: "After the
  // run" only over a board a run left with nothing pressed since (`coachHeading`).
  const heading = useCoachHeading()

  // Focused when the card APPEARS, once, rather than on every cue: stepping the
  // list with J and K must not yank focus back off a button the reader is
  // walking. The card is the answer to a run that has just finished, so it is
  // what a screen reader should land on and what Escape's first rung is about —
  // and `tabIndex={-1}` makes it focusable without adding a tab stop that leads
  // nowhere. `preventScroll` because the stage is a contained box: the browser's
  // own scroll-into-view would shift the whole field under the graph.
  //
  // Never out of a field, though. A run ends on its own schedule and the reader
  // may well be typing the next seed when it does; taking the caret out of the
  // composer mid-sentence to announce a card is the app interrupting a person
  // who was already working. The keyboard map is bound at the window, so nothing
  // is lost by leaving the caret where it is.
  useEffect(() => {
    const el = document.activeElement
    const typing =
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLElement && el.isContentEditable)
    if (!typing) elRef.current?.focus({ preventScroll: true })
  }, [elRef])

  const { cue, index, count, hidden, why, skippedOnce } = shown

  // The cue whose words are open in place, by key rather than as a flag: a step
  // to the next cue lands on it folded, with no effect to reset anything.
  const [openKey, setOpenKey] = useState<string | null>(null)
  const full = openKey === cue.key
  // The seed's title never opens in place. Its door is the prompt sheet.
  const seedTitle = session.nodes[cue.anchorId]?.parentId === null
  const titleClamped = seedTitle || !full
  const titleRef = useRef<HTMLElement | null>(null)
  const bodyRef = useRef<HTMLParagraphElement | null>(null)
  const titleCut = useCut(titleRef, titleClamped, cue.title)
  const bodyCut = useCut(bodyRef, !full, cue.body)
  // One door opens both blocks in place, and it stands under the first one cut:
  // under the body it would read as more of a body that is all there.
  const moreUnderTitle = !seedTitle && titleCut
  const more = (
    <button
      className="coach__more"
      aria-expanded={full}
      onClick={() => setOpenKey(full ? null : cue.key)}
    >
      {full ? 'Show less' : 'Show more'}
    </button>
  )

  const action = cue.action
  const routing = {
    session,
    modelId,
    roleModels,
    keys,
    customBase,
    customModel,
    preferCorpus,
    runCeiling,
    run,
    busy,
  }
  const auditor = cueAuditor(routing, cue)
  // The same reading `takeCueAction` guards on, so the disabled button and the
  // key press cannot come to disagree about whether this cue is pressable — and
  // the sentence printed below is the sentence the press would have raised,
  // rather than a second copy of it written for the card.
  const refusal = cueRefusal(routing, cue)
  const headline = useId()

  return (
    <section
      ref={elRef}
      className={`coach panel coach--${form}`}
      // Focusable, never a tab stop: see the effect above.
      tabIndex={-1}
      aria-label={heading}
      aria-describedby={headline}
      // Two variables, not one, per D16: the SURFACES of a cue (the icon, any
      // tint) take the mark colour the canvas paints that channel with, and the
      // TEXT takes the ink tier, which spends the hue's chroma on legibility at
      // 12px. One channel, two jobs.
      style={
        {
          '--cue-tint': `var(--sem-${cue.channel})`,
          '--cue-ink': `var(--ink-${cue.channel})`,
        } as CSSProperties
      }
    >
      <div className="coach__head">
        <span className="eyebrow">
          {heading} · <span className="num">{index + 1}</span> of{' '}
          <span className="num">{count}</span>
        </span>
        <button
          className="coach__skip"
          title={
            skippedOnce
              ? 'Skipping this kind again stops it appearing for the rest of this exploration.'
              : 'Answers this one and moves on. Nothing is spent.'
          }
          onClick={() => skipCue(cue)}
        >
          Skip
        </button>
      </div>

      {/* The card's one live region — see the component's comment. Atomic, so
          a step to a cue of the same kind still says which claim. */}
      <div id={headline} className="coach__headline" aria-live="polite" aria-atomic="true">
        <p className="coach__kind">{CUE_LABEL[cue.kind]}</p>
        <div className="coach__title">
          <Icon name="spark" size={14} />
          <strong
            ref={titleRef}
            data-clamp={titleClamped || undefined}
            data-cut={(titleClamped && titleCut) || undefined}
          >
            {cue.title}
          </strong>
        </div>
      </div>
      {/* Outside the live region, which holds words and no button: the door is
          read where the reader finds it, not announced with every step. */}
      {seedTitle && titleCut && (
        <button
          className="coach__more"
          title="The whole prompt, exactly as it was typed."
          onClick={() => setPromptOpen(true)}
        >
          Show more
        </button>
      )}
      {moreUnderTitle && more}
      <p ref={bodyRef} className="coach__body" data-clamp={!full || undefined}>
        {cue.body}
      </p>
      {!moreUnderTitle && (full || bodyCut) && more}
      <p className="coach__metric">{cue.metric}</p>

      {/* Not the replay position: the card renders only at now (`coachGateOpen`),
          so a fold or a switch is the whole of it. The promise that nothing has
          changed is kept by making the change a press — "Show it" turns on just
          the switches hiding this node or an ancestor and unfolds the way down
          (`showNode`, D335), rather than leaving the reader to work out which. */}
      {hidden && (
        <>
          <p className="coach__hidden">
            This node is folded away or hidden by a display switch. Nothing has been changed.
          </p>
          <div>
            <button className="btn" onClick={() => showNode(cue.anchorId)}>
              Show it
            </button>
          </div>
        </>
      )}
      {skippedOnce && (
        <p className="coach__hint">
          Skipped once already. Skip it again and this kind stops appearing in this exploration.
        </p>
      )}
      {/* The refusal, in the words the press itself would have used. Printed
          rather than paraphrased: the disabled button below and this paragraph
          are one reading, so the card cannot come to explain something other
          than what is stopping it. */}
      {refusal && <p className="coach__hint">{refusal}</p>}

      {why && (
        <div className="coach__why">
          <p className="coach__rule">{cue.rule}</p>
          <button className="btn" onClick={() => muteCuePress(cue)}>
            Stop showing this kind
          </button>
        </div>
      )}

      <div className="coach__actions">
        {/* At an end, shut rather than disabled: Next onto the last cue greyed the
            button under the key that pressed it, and focus fell to the page body.
            `aria-disabled` keeps it where it was, and the press guards itself. */}
        <button
          className="btn"
          aria-disabled={index === 0 || undefined}
          onClick={() => index > 0 && setCoachIndex(index - 1)}
        >
          Back
        </button>
        <button
          className="btn"
          aria-disabled={index >= count - 1 || undefined}
          onClick={() => index < count - 1 && setCoachIndex(index + 1)}
        >
          Next
          <Icon name="chevron" size={11} />
        </button>
        <span />
        <button
          className={why ? 'btn btn--on' : 'btn'}
          aria-expanded={why}
          title="The condition that fired this, with the thresholds it used."
          onClick={toggleCoachWhy}
        >
          Why
        </button>
        {/* No `busy` guard on the action: the gate this card renders behind
            already means no pass is in flight, so one here would be a condition
            that cannot be true. A refusal can. */}
        {action && (
          <button
            className="btn btn--outline"
            disabled={refusal !== null}
            // Its label, the claim it is aimed at, and the estimate or the status
            // word standing in for one — the figure itself shows only on hover.
            aria-label={cuePressName(cue, cuePressTail(routing, cue, auditor?.entry ?? null))}
            onClick={() => takeCueAction(cue)}
          >
            <CuePressText cue={cue} auditor={auditor?.entry ?? null} />
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * The popup: the card beside its node, placed by the canvas's own frame loop.
 *
 * Mounted at App level in the stage, beside `CoachPins` and under the same rule
 * — React renders the content, the tick below writes the seat's transform, and
 * nothing here re-renders while the camera is moving. The reads are all
 * imperative for the same reason the pins' are: a pane opening has to change the
 * answer on the very next FRAME, not on the next React render.
 *
 * The breakpoint is not asked here. App mounts this above 768px and `RunDoneBar`
 * mounts the column form below it, from the two components' own readings of one
 * layout — and a third component matching the same media query separately is how
 * they would come to disagree about which layout is on screen.
 *
 * The positions that come out of one tick:
 *
 *   BESIDE the node — RIGHT of it when the anchor sits in the left
 *   `CARD_FLIP_AT` of the FIELD, LEFT of it otherwise, `CARD_GAP` off it with
 *   the hairline across the gap. The side and the clamp are one arithmetic in
 *   `coachPlace.ts`: measured against different widths they disagreed, and the
 *   card was assigned a side with no room on it and then clamped back on top
 *   of its own anchor.
 *   BELOW, ABOVE, or in a CORNER of the field, with no hairline — only where
 *   beside would stand on a node the cue describes: its anchor on a field too
 *   narrow to clear it sideways, or the objection, the newer record, the far
 *   end of a join sitting where the side position lands (`cardPlace`). The
 *   card never opens over the claims it is talking about while any spot in the
 *   field is clear of them.
 *   FALLBACK, under the run-done bar's column at the column's own left edge,
 *   whenever there is no anchor to stand beside: `place().on` false, or a node
 *   behind a fold or a display switch. The card never
 *   simply vanishes while it is open and there are cues — a card with nowhere to
 *   point still has a sentence to print, and it prints the hidden-anchor note
 *   with it.
 *
 * Off-stage is NOT a fallback. The director is usually flying the camera toward
 * this very node, so the anchor arrives from outside the frame; the clamps hold
 * the card at the edge nearest it and it slides into place, which is continuous
 * where a form change would flicker.
 *
 * The clamps are what make the first two placements safe. Horizontally the card
 * stays inside `[16, stageW - viewInset.right - 16]`, so it never slides under
 * the inspector's glass when the rail is open — and the side is chosen against
 * that same window, so the clamp never has to push the card back over the node
 * it was placed beside; vertically it stays inside a band
 * measured per frame between the run-done column above it and the field's own
 * bottom furniture below (`CARD_FLOOR`), and the height ceiling is that band, so
 * a WHY fold on a short stage scrolls inside the card rather than carrying the
 * action row off the bottom.
 */
export function CoachPopup() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const seatRef = useRef<HTMLDivElement | null>(null)
  const cardRef = useRef<HTMLElement | null>(null)
  const shown = useShownCue()
  // Only the two facts the placement depends on go in the deps below. The cue's
  // words change under it without re-subscribing, which is the point.
  const anchorId = shown?.cue.anchorId ?? null
  const hidden = shown?.hidden ?? false
  // The other nodes the card must not stand on, handed to the tick through a ref:
  // a fold opening changes which are drawn, and that must reach the next frame
  // without tearing down a subscription whose `prev` spot is what keeps the card
  // from hopping (`cardPlace`).
  const alsoRef = useRef<string[]>([])
  alsoRef.current = shown?.alsoIds ?? []

  useEffect(() => {
    if (!anchorId) return
    // Written only when the answer changes: the tick runs every frame, and a
    // style write per frame invalidates layout per frame for a card that spends
    // most of its life sitting still.
    let lastT = ''
    let lastSide = ''
    let lastMaxH = ''
    let placed = false
    // Where the card stood last frame, so a spot that is still clear is kept
    // rather than traded for another equally clear one mid-flight.
    let spot: CardSpot | null = null

    const tick = () => {
      const host = hostRef.current
      const seat = seatRef.current
      const card = cardRef.current
      if (!host || !seat || !card) return
      const W = host.clientWidth
      const H = host.clientHeight
      if (W <= 0 || H <= 0) return
      const hr = host.getBoundingClientRect()

      // The run-done column, MEASURED rather than recomputed. Its left edge is
      // whatever `RunDoneBar` resolved — beside the session column, below it when
      // the span is cramped, or the phone's full-width form — and its bottom is
      // wherever the bar and any digest actually ended. One source for those
      // edges; this reads the box they produced rather than keeping a second
      // copy of the arithmetic that decides them.
      const col = document.querySelector('.rundonecol')?.getBoundingClientRect()
      const hasCol = !!col && col.width > 0 && col.height > 0
      const colL = hasCol ? col.left - hr.left : CARD_EDGE
      const minTop = (hasCol ? col.bottom - hr.top : CARD_EDGE) + CARD_BAR_GAP

      // The columns the whole app agrees the two panes take — wider than the
      // glass itself, and the same reading the camera centres against and the
      // bar's edges ride. On a phone both are 0 by construction (the inspector
      // is a bottom sheet, the sidebar an overlay) and this component is not
      // mounted there anyway.
      const { left: insetLeft, right: paneRight } = useStore.getState().viewInset
      // The rendered position, from whichever camera just painted.
      const q = hidden ? null : screen.place(anchorId)
      // The width is fixed by the stylesheet, so it can be read before the
      // height ceiling below is decided — and the ceiling needs the horizontal
      // position, because which chrome the card meets depends on where it stands.
      const cw = seat.offsetWidth

      // The band the card may stand in, decided against the chrome it would
      // otherwise be buried under. This is the pins' CHROME argument with the one
      // difference that matters: a pin under a panel can HIDE, because J, K and
      // the digest still reach that cue without it — a card is the reading
      // itself, so it moves instead. Only the boxes a card standing at `left`
      // actually meets, and each along the axis it holds: the top-left stack
      // presses the ceiling down, the field's bottom furniture lifts the floor.
      // Measured every frame, never assumed at a corner — all of it moves with
      // the panes' insets and some of it is not drawn at all on a narrow stage.
      // A function of the left edge, because the placement below asks it once
      // per place the card could stand, and the chrome differs between them —
      // over boxes measured ONCE per frame, so asking it eight times reads the
      // DOM no more often than asking it once did.
      const rectOf = (sel: string) => {
        const r = document.querySelector(sel)?.getBoundingClientRect()
        if (!r || r.width <= 0 || r.height <= 0) return null
        return {
          l: r.left - hr.left,
          r: r.right - hr.left,
          top: r.top - hr.top,
          bottom: r.bottom - hr.top,
        }
      }
      const headBox = rectOf('.slot-session')
      const floorBoxes = CARD_FLOOR.map(rectOf)
      // The tool dock stands on the field's right edge, and the pins already hide
      // beneath it (`CHROME` in CoachPins.tsx). A card is the reading itself, so
      // it keeps out instead: the dock's column joins the right inset for every
      // place the card could stand. Without it, a card beside a node near that
      // edge was clamped back under the dock, and back against its own node — the
      // camera centres the field since D159, which put nodes there more often.
      const dockBox = rectOf('.tooldock')
      const insetRight =
        dockBox && dockBox.l > W / 2 ? Math.max(paneRight, W - dockBox.l) : paneRight
      const band = (left: number): Band => {
        const meets = (box: ReturnType<typeof rectOf>) =>
          box && !(left >= box.r || left + cw <= box.l) ? box : null
        const head = meets(headBox)
        // The run-done column's own clearance is NOT conditional on meeting it:
        // the card is the row under that report at every width, and the fallback
        // below seats itself directly beneath it.
        let ceil = minTop
        if (head) ceil = Math.max(ceil, head.bottom + CARD_BAR_GAP)
        let floor = H - CARD_EDGE
        for (const b of floorBoxes) {
          const box = meets(b)
          if (box) floor = Math.min(floor, box.top - CARD_BAR_GAP)
        }
        return { ceil, floor }
      }

      let left: number
      let maxH: number
      let aimed = -1
      let side = ''
      if (q?.on) {
        // Clear of every node the cue describes, not only the anchor
        // (`coachPlace.ts`). Beside the node first, the design's own position —
        // and side and clamp are still ONE arithmetic there, because they were
        // two once and disagreed exactly where it hurt: the side was chosen
        // against the full stage while the clamp read the stage minus the
        // inspector's column, so an anchor with no room on its right was sent
        // right anyway and clamped back on top of itself. Then below, above, and
        // the field's corners, for the cue whose objection or newer record sits
        // where the beside position would have stood — which is where a study
        // watched the card open, over the very claims it was describing.
        const also: Point[] = []
        for (const id of alsoRef.current) {
          const p = screen.place(id)
          if (p.on) also.push({ x: p.x, y: p.y })
        }
        const at = cardPlace({
          anchor: { x: q.x, y: q.y },
          also,
          stageW: W,
          insetLeft,
          insetRight,
          cardW: cw,
          // The height the content wants, whatever ceiling last frame wrote:
          // the placement decides that ceiling, so it cannot read it back.
          cardH: card.scrollHeight + (card.offsetHeight - card.clientHeight),
          band,
          prev: spot,
        })
        spot = at.spot
        left = at.left
        maxH = at.maxH
        aimed = at.top
        // The hairline belongs to the two beside positions: it crosses the
        // `CARD_GAP` a side keeps from its node. Below, above or in a corner there
        // is no gap of that shape to cross.
        side = at.spot === 'right' || at.spot === 'left' ? at.spot : ''
      } else {
        // The fallback takes the same window, even though it borrows its edge
        // from a column that is itself inset from that same glass (`RunDoneBar`)
        // and so cannot cross it today. That is a fact about another file's
        // constants — the width it calls cramped, the pad it keeps — and the
        // rule being expressed here is this card's own. Leaving the branch
        // unclamped would make a geometry over there load bearing over here,
        // silently, which is the coupling this whole re-homing was about.
        left = clampCardX(colL, W, insetLeft, insetRight, cw)
        const { ceil, floor } = band(left)
        maxH = Math.max(CARD_MIN_H, floor - ceil)
      }

      // The card scrolls inside itself rather than growing past that band: a WHY
      // fold on a short stage is the case that produced this, and a box that runs
      // off the bottom takes its action row with it. The floor is a floor for the
      // HEIGHT before it is one for the position, so a card that cannot fit
      // shortens rather than sliding under the composer.
      const maxHpx = `${Math.round(maxH)}px`
      if (maxHpx !== lastMaxH) {
        card.style.maxHeight = maxHpx
        lastMaxH = maxHpx
      }
      const ch = seat.offsetHeight
      const { ceil, floor } = band(left)

      let top: number
      let connY = -1
      if (q?.on) {
        // Where the placement aimed, held inside the band against the height the
        // card actually took — the two differ by a border's rounding at most.
        top = Math.min(Math.max(aimed, ceil), Math.max(ceil, floor - ch))
        // The hairline meets the node, not the card's midpoint: once either
        // clamp has bitten, the anchor is no longer level with the middle of the
        // box. Kept a few pixels off the corners, where a 1px line reads as a
        // stray mark rather than as a pointer.
        if (side) connY = Math.min(Math.max(q.y - top, 10), Math.max(10, ch - 10))
      } else {
        top = ceil
      }

      const t = `translate(${left.toFixed(1)}px,${top.toFixed(1)}px)`
      if (t !== lastT) {
        seat.style.transform = t
        lastT = t
      }
      if (side !== lastSide) {
        // The connector belongs to an anchored card only. `data-side` carries
        // both facts — which edge it leaves from, and whether there is one at
        // all — so the fallback and the phone's column form drop it by having no
        // attribute rather than by a second condition in CSS.
        if (side) seat.dataset.side = side
        else delete seat.dataset.side
        lastSide = side
      }
      if (connY >= 0) seat.style.setProperty('--cue-conn', `${connY.toFixed(1)}px`)
      // Revealed once the loop has actually placed it: the element exists the
      // moment React commits it, and without this it paints one frame in the
      // stage's top-left corner. Written once, like everything else here — the
      // same value re-set every frame is a style invalidation per frame for a
      // fact that changes once.
      //
      // An ATTRIBUTE the stylesheet reads, rather than the `visibility` a pin
      // writes for the same job, and the difference is the card's focus. A pin
      // hides with `visibility` precisely so it leaves the tab order with its
      // paint; the card must stay focusable through this frame, because it takes
      // focus on the commit — before the canvas has drawn the frame this
      // callback rides. Under `visibility: hidden` that focus is refused with no
      // error, and the card ends up announcing nothing and answering no Escape.
      // The stylesheet carries the rest (`.coachpop__seat`), including the fade,
      // so the reveal is one fact written in one place.
      if (!placed) {
        seat.dataset.placed = ''
        placed = true
      }
    }

    // Driven by the canvas's loop, not one of our own — the card has to be
    // placed with the very camera the strands were just painted with, or it
    // slides off its node whenever the view is moving.
    return onAfterDraw(tick)
  }, [anchorId, hidden])

  if (!shown) return null

  return (
    <div className="coachpop" ref={hostRef}>
      <div
        className="coachpop__seat"
        ref={seatRef}
        // The tint rides the seat rather than the card, so the hairline outside
        // the card's box inherits the same channel the card's own glyph takes.
        style={{ '--cue-tint': `var(--sem-${shown.cue.channel})` } as CSSProperties}
      >
        <span className="coachpop__link" aria-hidden />
        <CoachCardBody shown={shown} elRef={cardRef} form="popup" />
      </div>
    </div>
  )
}

/**
 * The phone's form: a row in the run-done bar's own column.
 *
 * Mounted by `RunDoneBar` INSIDE that column, which is the whole point — the
 * column is one positioned box owning one measurement of the field's edges, and
 * its rows share them by construction. A card that measured its own would be the
 * second copy of a geometry that is already hard-won (the session column's
 * width, the phone's below-the-column drop, the cramped-span fallback).
 *
 * No anchoring and no connector: there is no room beside a node at this width,
 * and this is also what retired the phone-reachability workaround. The pin, STEP
 * THROUGH and the digest's read row each used to open the inspector because the
 * card was inside it and nothing else on a phone would raise the sheet. The card
 * is not in there any more, so that press is dead coupling and it is gone.
 *
 * The column owns three of this row's four edges and cannot own the fourth. It
 * is a top-anchored stack — `left`, `right`, and a `top` under the session
 * column — so its rows are bounded above and open below, and this row is the one
 * that grows: a long cue, or the WHY fold opening under it, ran the box down
 * over the composer — a composer that is not behind anything, its tune controls
 * left untappable underneath the card's own action row (and, while the card was
 * glass, reading through it). So the floor is measured here, against the
 * same `CARD_FLOOR` furniture the popup holds itself off, and the card scrolls
 * inside itself exactly as the popup does. Not a second copy of the column's
 * geometry: the top edge is READ back off this element, whatever the column
 * resolved it to be, and only the bottom — which the column has no opinion about
 * — is decided here.
 */
export function CoachColumnCard() {
  const cardRef = useRef<HTMLElement | null>(null)
  const shown = useShownCue()
  // The card's presence, not its contents: the ceiling depends on where the
  // column put this row and what is drawn under it, and on neither the cue's
  // words nor its index. Stepping the list with J and K must not resubscribe.
  const on = shown !== null

  useEffect(() => {
    if (!on) return
    let lastMaxH = ''
    const tick = () => {
      const card = cardRef.current
      if (!card) return
      // Viewport coordinates throughout: the column is `position: fixed`, so the
      // stage's own box is not the frame either of these boxes is placed in.
      // The card's top is read rather than recomputed — the column decides it,
      // and a copy of that decision here is the drift this file keeps refusing.
      // Reading it back cannot loop with the write below: the row is the last in
      // a top-anchored stack, so its own height moves nothing above it.
      const top = card.getBoundingClientRect().top
      let floor = window.innerHeight - CARD_EDGE
      for (const sel of CARD_FLOOR) {
        const r = document.querySelector(sel)?.getBoundingClientRect()
        // A zero-size rect is chrome that is present but not laid out, and the
        // minimap and the dock are not drawn at all at this width. No element,
        // no rect — the same reading the popup's tick and the pins' both take.
        if (!r || r.width <= 0 || r.height <= 0) continue
        floor = Math.min(floor, r.top - CARD_BAR_GAP)
      }
      // Short of the floor by `DOCK_ROOM`: a docked card cannot step aside from
      // its node the way the popup does, so it leaves a strip free between itself
      // and the composer, and the director walks the node into that strip. A card
      // that ran down to the composer would open over the claim it describes —
      // the phone is where a study watched exactly that.
      const maxH = `${Math.round(Math.max(CARD_MIN_H, floor - top - DOCK_ROOM))}px`
      // Written only when the answer changes: this runs every frame, and a style
      // write per frame invalidates layout per frame for a box that spends most
      // of its life exactly where it was.
      if (maxH !== lastMaxH) {
        card.style.maxHeight = maxH
        lastMaxH = maxH
      }
    }
    // The canvas's loop again, for the ordinary reason the popup uses it: the
    // composer's own height changes with the text in it, and the run pod above
    // this column comes and goes, so the floor and the top are both live facts
    // rather than facts about the mount. There is no camera in this one — the
    // card is not anchored to anything at this width — only the clock.
    return onAfterDraw(tick)
  }, [on])

  if (!shown) return null
  return <CoachCardBody shown={shown} elRef={cardRef} form="column" />
}

/**
 * Where the node should land when the card is DOCKED — the phone's form, a row
 * in the run-done column — or null when it is not.
 *
 * The popup steps aside from its node; a docked card cannot, and the middle of a
 * phone's stage is routinely under it. So the node goes to the middle of the
 * strip the column leaves free above the field's bottom furniture (the same
 * `CARD_FLOOR` the card holds itself off, `DOCK_ROOM` of it at least). Read off
 * the DOM rather than a media query: what matters is whether a docked card is on
 * screen, and asking the breakpoint a third time is how the layouts would come
 * to disagree (see `CoachPopup`). Stage coordinates, because `centerOn` is.
 */
function dockedStripMiddle(): number | null {
  const card = document.querySelector('.coach--column')?.getBoundingClientRect()
  const stage = document.querySelector('main.stage')?.getBoundingClientRect()
  if (!card || !stage || card.height <= 0) return null
  const top = card.bottom - stage.top
  let floor = stage.height - CARD_EDGE
  for (const sel of CARD_FLOOR) {
    const r = document.querySelector(sel)?.getBoundingClientRect()
    if (!r || r.width <= 0 || r.height <= 0) continue
    floor = Math.min(floor, r.top - stage.top - CARD_BAR_GAP)
  }
  return floor > top ? (top + floor) / 2 : null
}

/**
 * Walk the camera to the cue the card is showing, without taking the view.
 *
 * No select, no focus, no unfold, no filter change — those are the reader's
 * working state — and `byUser: false` throughout so a direct gesture still takes
 * the camera back.
 */
export function CoachDirector({ stageW, stageH }: { stageW: number; stageH: number }) {
  const viewMode = useStore((s) => s.viewMode)
  const coachIndex = useLiveCoach()?.index ?? 0
  const cues = useVisibleCues()
  // The card's own arm, from the helper the card mounts on, rather than a fourth
  // spelling of it: the camera must not walk to a node whose card is not there.
  const shows = useCardShows()
  const { visible } = useGraphFrame()

  // The card's clamp, once more: the two must never walk to different nodes.
  const shown = shows ? cues[Math.min(Math.max(0, coachIndex), cues.length - 1)]! : null
  const targetId = shown?.anchorId ?? null

  useEffect(() => {
    if (!targetId || stageW <= 0 || stageH <= 0) return
    // Two steps used to stand here and both have gone with the re-homing: open
    // the inspector, then wait for App's `viewInset.right` before centring, or
    // the first camera target lands under the glass that had just come on
    // screen. Neither was about the CAMERA — they were there because the card
    // was rendered inside that pane, so the director had to bring the pane on
    // screen for the card to exist and had to let its column settle before
    // choosing where to put the node. The card is a popup on the graph now: it
    // needs no pane, and the inset it must not stand under is read per frame by
    // the popup's own tick against whatever the rail happens to be doing.
    //
    // The phone's exemption went with them, which is the same simplification
    // read from the other end: there was never an inset to wait for at that
    // width (the inspector is a bottom sheet, so App holds the inset at 0 by
    // construction), and now there is nothing to wait for at any width. One path
    // for both layouts.
    if (!visible.has(targetId)) return

    // The flat canvas consumes a revision by following the whole graph — the
    // node that just arrived, the fold that just opened. Run this one frame
    // later so the editorial target is the last camera writer, without
    // pretending the reader pinned the view themselves.
    const raf = requestAnimationFrame(() => {
      const current = useStore.getState()
      const held = liveCoach(current)
      if (!held?.open) return
      // The staleness check: the store can move between this effect and the
      // frame it asked for, and the cleanup that cancels this callback is not
      // guaranteed to run before the frame does. Walking to the anchor that WAS
      // current and then to the one that is now is a camera fighting itself, so
      // the frame re-asks which cue is showing — through the same helper, so it
      // cannot get a different answer.
      const live = visibleCues(current)
      const now = live[Math.min(Math.max(0, held.index), live.length - 1)]
      if (now?.anchorId !== targetId) return

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (viewMode === '3d') {
        current.pivotOn(targetId, false)
        if (reduced) {
          const target = useStore.getState().camTarget
          if (target) useStore.getState().applyCam(target, true)
        }
        return
      }

      const zoom = Math.max(current.view.k, 0.68)
      // `centerOn` seats the node at half the height it is handed, so handing it
      // twice the free strip's middle seats the node there — below the docked
      // card rather than under it. Anywhere else the stage's own middle, which
      // the popup then stands clear of by itself.
      const docked = dockedStripMiddle()
      current.centerOn(targetId, stageW, docked !== null ? 2 * docked : stageH, zoom, false)
      if (reduced) {
        const target = useStore.getState().viewTarget
        if (target) useStore.getState().applyView(target, true)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [targetId, stageW, stageH, viewMode, visible])

  return null
}

/** The lens cycle, in one place: the bar's ORDER button and the `O` key press
 *  the same rotation, and its labels are the bar's. */
export const COACH_LENSES = ['risk', 'cheapest', 'experiment'] as const
export const COACH_LENS_LABEL: Record<(typeof COACH_LENSES)[number], string> = {
  risk: 'BIGGEST RISK',
  cheapest: 'CHEAPEST FIRST',
  experiment: 'CLOSEST TO A TEST',
}

/** Advance the order lens by one. */
export function cycleCoachLens(): void {
  const s = useStore.getState()
  const coach = liveCoach(s)
  if (!coach) return
  const i = COACH_LENSES.indexOf(coach.lens)
  s.setCoachLens(COACH_LENSES[(i + 1) % COACH_LENSES.length]!)
}
